/**
 * ClosedCardRowView — one closed player card plus its reopen affordances
 * (SE-6862, Figma nodes 80:18297 / 80:18298).
 *
 * The card itself is the shared Player Card v7 renderer, so a closed player
 * reads exactly as it does on the board — the design's whole point is that
 * closing a card files it away rather than transforming it. What this row adds
 * is the pair of ways back out: an inline "Reopen…" link that appears on hover,
 * and an overflow menu carrying the same action.
 *
 * Both exist on purpose. The inline link is the design's affordance and stays
 * quiet until the pointer is on the card; the overflow trigger is always
 * visible, which is what makes the action reachable by touch, by keyboard, and
 * by a test that cannot hover. Neither is a fallback for the other.
 * @module features/player-kanban/components/organisms/ClosedArea/ClosedCardRow/ClosedCardRowView
 */

import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";
import { MoreHorizontal, Undo2 } from "lucide-react-native";
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import DesktopDropdownMenu, {
  type DesktopDropdownMenuItem,
} from "@/components/molecules/DesktopDropdownMenu";
import MobileTargetCardView from "@/features/scouting/components/organisms/MobileTargetCard/MobileTargetCardView";

/** The v7 card's full prop surface, as the adapter must satisfy it. */
export type ClosedPlayerCardProps = React.ComponentProps<
  typeof MobileTargetCardView
>;

/**
 * Reveal classes for the inline reopen link.
 *
 * Web-only by construction: the link fades in when the pointer enters the card
 * (a NAMED group, so an ancestor's group cannot drive it) and also on keyboard
 * focus, so tabbing to it does not land on something invisible. On native there
 * is no hover at all, so none of these apply and the link simply renders — which
 * is the correct mobile behaviour, not a degradation.
 */
const REOPEN_REVEAL_CLASS =
  "web:opacity-0 web:transition-opacity web:group-hover/closedcard:opacity-100 web:focus-visible:opacity-100";

/** Props for {@link ClosedCardRowView}. */
export interface ClosedCardRowViewProps {
  /** The v7 card's props, adapted from the closed card by the container. */
  readonly cardProps: ClosedPlayerCardProps;
  /** Player display name, used for the actions' accessible names. */
  readonly playerName: string;
  /** Whether the card renders de-emphasised (a non-signed outcome). */
  readonly dimmed: boolean;
  /** Whether the viewer may reopen — false hides the whole action row. */
  readonly canReopen: boolean;
  /** Reopen this card. */
  readonly onReopen: () => void;
  /** Whether the overflow menu is open. */
  readonly isMenuOpen: boolean;
  /** Toggle the overflow menu. */
  readonly onMenuToggle: () => void;
  /** Close the overflow menu. */
  readonly onMenuClose: () => void;
  /** The overflow-menu rows, built by the container. */
  readonly menuItems: readonly DesktopDropdownMenuItem[];
}

/**
 * Renders one closed card with its reopen affordances.
 * @param props - {@link ClosedCardRowViewProps}
 * @param props.cardProps - Adapted v7 card props
 * @param props.playerName - Player display name
 * @param props.dimmed - Whether to de-emphasise the card
 * @param props.canReopen - Whether the viewer may reopen
 * @param props.onReopen - Reopen handler
 * @param props.isMenuOpen - Whether the overflow menu is open
 * @param props.onMenuToggle - Overflow-menu toggle
 * @param props.onMenuClose - Overflow-menu close
 * @param props.menuItems - Overflow-menu rows
 * @returns The closed card row.
 */
const ClosedCardRowView = ({
  cardProps,
  playerName,
  dimmed,
  canReopen,
  onReopen,
  isMenuOpen,
  onMenuToggle,
  onMenuClose,
  menuItems,
}: ClosedCardRowViewProps) => (
  /* The named group is declared here, on the whole row, so hovering anywhere
       over the card — not just over the link — reveals the reopen affordance.
       `opacity-85` is the design's de-emphasis for a non-signed outcome
       (Figma 80:18304): closed-and-not-signed is still history worth reading,
       so it recedes rather than greying out. */
  <Box
    className={`group/closedcard w-full ${dimmed ? "opacity-85" : ""}`.trim()}
  >
    <MobileTargetCardView {...cardProps} />
    {canReopen ? (
      <Stack
        direction="horizontal"
        className="w-full items-center justify-between px-4"
      >
        <Pressable
          testID={TEST_ID_PLAYER_KANBAN.REOPEN_CARD_BUTTON}
          onPress={onReopen}
          accessibilityRole="button"
          accessibilityLabel={`Reopen ${playerName}`}
          className={REOPEN_REVEAL_CLASS}
        >
          <Stack direction="horizontal" className="items-center gap-1">
            <Icon as={Undo2} size="xs" className="text-accent-primary" />
            <Text variant="micro" className="text-accent-primary">
              Reopen…
            </Text>
          </Stack>
        </Pressable>
        <DesktopDropdownMenu
          isOpen={isMenuOpen}
          onClose={onMenuClose}
          onToggle={onMenuToggle}
          placement="bottom-end"
          triggerTestID={TEST_ID_PLAYER_KANBAN.CLOSED_CARD_MENU}
          triggerAccessibilityLabel={`Actions for ${playerName}`}
          triggerClassName="rounded-sm p-1"
          triggerContent={
            <Icon
              as={MoreHorizontal}
              size="sm"
              className="text-content-secondary"
            />
          }
          items={menuItems}
        />
      </Stack>
    ) : null}
  </Box>
);

ClosedCardRowView.displayName = "ClosedCardRowView";

export default memo(ClosedCardRowView);
